import type {
  HilPresentationRequestEnvelope,
  HilPresentationAction,
  HilPresentation,
} from '@bizfirst/hil-types';

/**
 * Parses the `ReceiveServerEvent` SignalR payload shape (EdgeStream v2: `dataEnvelope.data` +
 * `dataEnvelope.eventInfo`), used by every ProcessEngine execution stream regardless of which UI
 * consumes it (Flow Studio's own execution observer, WorkDesk's HIL inbox, ChatDesk's single-
 * conversation window). Previously reimplemented inline in flow-studio-designer's own
 * `useExecutionSignalR.ts` — moved here 2026-09-12 so ChatDesk (and any future `ReceiveServerEvent`
 * consumer) reuses the exact same, already-proven parsing instead of a second hand-copy drifting
 * out of sync with it. Flow Studio's hook now imports these instead of defining its own.
 */

type Json = Record<string, unknown>;

function asObj(v: unknown): Json | undefined {
  return v && typeof v === 'object' ? (v as Json) : undefined;
}

/**
 * Extracts `HilInboxReferences` from a "progressing" `ReceiveServerEvent` payload, if the event
 * carries an embedded HIL suspend (e.g. `Stage=HIL, SubStage=Suspended`). Returns `null` for the
 * overwhelming majority of progressing events, which carry no HIL info at all — callers must treat
 * that as "not a HIL event," not an error.
 */
export function extractHilPresentationInfo(data: unknown, eventInfo: unknown): Json | null {
  const ei = asObj(eventInfo);
  const ed = asObj(ei?.eventDescription ?? ei?.EventDescription);
  const meta = asObj(ed?.metadata ?? ed?.Metadata);
  if (meta?.hilInboxReferences) return asObj(meta.hilInboxReferences) ?? null;
  if (meta?.HilInboxReferences) return asObj(meta.HilInboxReferences) ?? null;
  const d = asObj(data);
  if (d?.hilInboxReferences) return asObj(d.hilInboxReferences) ?? null;
  return null;
}

/**
 * Backend sends camelCase JSON on some paths and PascalCase on others (sometimes both within the
 * same envelope's nesting levels); `HilPresentationRequestEnvelopeReader`/`HilEnvelopeValidator`
 * require the PascalCase `HilPresentationRequestEnvelope` shape. Normalizes either into that shape.
 */
export function normalizeHilEnvelope(raw: unknown): HilPresentationRequestEnvelope {
  const r = asObj(raw) ?? {};
  const s = asObj(r.Session ?? r.session) ?? {};

  const mapAction = (a: Json): HilPresentationAction => ({
    ActionKey: (a.ActionKey ?? a.actionKey) as string,
    Label: (a.Label ?? a.label ?? a.ActionLabel ?? a.actionLabel) as string | undefined,
    Style: (a.Style ?? a.style) as HilPresentationAction['Style'],
    IsPrimary: (a.IsPrimary ?? a.isPrimary) as boolean | undefined,
    Decision: (a.Decision ?? a.decision) as string | undefined,
    DisplayOrder: (a.DisplayOrder ?? a.displayOrder) as number | undefined,
    RequiresInput: (a.RequiresInput ?? a.requiresInput) as boolean | undefined,
    InputLabel: (a.InputLabel ?? a.inputLabel) as string | undefined,
    InputRequired: (a.InputRequired ?? a.inputRequired) as boolean | undefined,
    RequiresReason: (a.RequiresReason ?? a.requiresReason) as boolean | undefined,
    RequiredRole: (a.RequiredRole ?? a.requiredRole) as string | undefined,
    Enabled: (a.Enabled ?? a.enabled) as boolean | undefined,
    Description: (a.Description ?? a.description) as string | undefined,
    Config: (a.Config ?? a.config) as Record<string, unknown> | undefined,
  });

  const rawPresentations = (r.Presentations ?? r.presentations) as Json[] | undefined;
  const rawRenderOptions = (r.RenderOptions ?? r.renderOptions) as Json[] | undefined;
  const presentationSource: Json[] = rawPresentations?.length ? rawPresentations : (rawRenderOptions ?? []);

  const presentations: HilPresentation[] = presentationSource.map((p) => ({
    PresentationType: (p.PresentationType ?? p.presentationType ?? p.RenderType ?? p.renderType) as string,
    Order: (p.Order ?? p.order ?? 1) as number,
    Title: (p.Title ?? p.title) as string | undefined,
    FormID: (p.FormID ?? p.formID) as number | undefined,
    FormSchemaJson: (p.FormSchemaJson ?? p.formSchemaJson) as string | undefined,
    PrePopulate: (p.PrePopulate ?? p.prePopulate ?? {}) as Record<string, unknown>,
    PromptMessage: (p.PromptMessage ?? p.promptMessage) as string | undefined,
    SummaryText: (p.SummaryText ?? p.summaryText) as string | undefined,
    ContextDocURL: (p.ContextDocURL ?? p.contextDocURL) as string | undefined,
    Actions: ((p.Actions ?? p.actions ?? []) as Json[]).map(mapAction),
    CustomData: (p.CustomData ?? p.customData) as Record<string, unknown> | undefined,
  }));

  return {
    HilType: (r.HilType ?? r.hilType) as string,
    Title: (r.Title ?? r.title) as string | undefined,
    Description: (r.Description ?? r.description) as string | undefined,
    Session: {
      EngageSessionID: (s.EngageSessionID ?? s.engageSessionID) as number,
      NodeKey: (s.NodeKey ?? s.nodeKey) as string | undefined,
      WorkflowID: (s.WorkflowID ?? s.workflowID) as string | undefined,
      TenantID: (s.TenantID ?? s.tenantID) as number | undefined,
      CorrelationID: (s.CorrelationID ?? s.correlationID) as string,
      ResumeCallbackURL: (s.ResumeCallbackURL ?? s.resumeCallbackURL) as string,
      ProcessElementID: (s.ProcessElementID ?? s.processElementID) as string | undefined,
      // No-Engage resume path (e.g. Octopus AI Agent chat, where EngageSessionID is always 0) —
      // the thread-level ID HilReplyContinuationService's no-Engage branch resumes on instead of
      // an EngageSessionID lookup. Additive/optional — absent/no-op for every session that has a
      // real EngageSessionID.
      ExecutionResID: (s.ExecutionResID ?? s.executionResID) as string | undefined,
    },
    Assignees: ((r.Assignees ?? r.assignees ?? []) as Json[]).map((a) => ({
      ActorID: (a.ActorID ?? a.actorID) as string,
      ActorType: (a.ActorType ?? a.actorType) as string | undefined,
      DisplayName: (a.DisplayName ?? a.displayName) as string | undefined,
    })),
    Channels: ((r.Channels ?? r.channels ?? []) as Json[]).map((c) => ({
      ChannelType: (c.ChannelType ?? c.channelType) as string,
      Name: (c.Name ?? c.name) as string | undefined,
      ChannelFormat: (c.ChannelFormat ?? c.channelFormat) as string | undefined,
      IsPrimary: (c.IsPrimary ?? c.isPrimary) as boolean | undefined,
    })),
    Presentations: presentations,
    Actions: ((r.Actions ?? r.actions ?? []) as Json[]).map(mapAction),
    ReplyOptions: (r.ReplyOptions ?? r.replyOptions) as HilPresentationRequestEnvelope['ReplyOptions'],
    Metadata: (r.Metadata ?? r.metadata) as Record<string, string> | undefined,
    CustomData: (r.CustomData ?? r.customData) as Record<string, unknown> | undefined,
  };
}

/**
 * Backend builds ResumeCallbackURL before the EngageSession row exists, so it bakes in SessionID 0.
 * Rebuilds it from the real EngageSessionID once known. `/api/hil/respond/{id}` handles every HIL
 * type (form, approval, chat) — `/api/v1/ai/inbox-items/{id}/act` is approval-only. Expects an
 * already-PascalCase-normalized envelope (i.e. run through `normalizeHilEnvelope` first).
 */
export function patchEnvelopeResumeUrl(envelope: HilPresentationRequestEnvelope): HilPresentationRequestEnvelope {
  const engageSessionID = envelope?.Session?.EngageSessionID;
  if (!engageSessionID) return envelope;
  return {
    ...envelope,
    Session: {
      ...envelope.Session,
      ResumeCallbackURL: `/api/hil/respond/${engageSessionID}`,
    },
  };
}
